import http from 'apis/config/http-service';
import { useMemo } from 'react';
import { useQueries } from 'react-query';
import type { Paginated, QuotationSupplierProduct } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET .../quotation-suppliers/:supplierId/products for a SET of suppliers,
// indexed by (supplier, master product). The match matrix uses this to enrich a
// quote with the origin/state/specs and ladder of the master product quoted,
// where the quote's own product summary lacks them (older quotes). It reflects
// the catalogue as of the last fetch, not a snapshot taken at quote time —
// refetchOnWindowFocus below (a deliberate per-query override of the app-wide
// `false`) picks up edits made on the Prices screen when ops switches back.
const quotationSuppliersEndpoint = `${restaurantInquiriesEndpoint}/quotation-suppliers`;

// 100 is the endpoint's hard max. The supplier's priced master products come
// first, so one page covers what a supplier quotes; a product past it simply
// stays un-enriched ('—' cells, excluded from the score) — never wrong, just absent.
const PAGE_LIMIT = 100;

/** The index key of a supplier's quotable master product. */
export const quotableProductKey = (supplierId: number, masterProductId: number) => `${supplierId}:${masterProductId}`;

export function useQuotationSupplierProductIndexApi(supplierIds: number[]): Map<string, QuotationSupplierProduct> {
  const results = useQueries(
    supplierIds.map((sid) => ({
      queryKey: [quotationSuppliersEndpoint, sid, 'products', { limit: PAGE_LIMIT }],
      queryFn: async (): Promise<Paginated<QuotationSupplierProduct>> => {
        const response = await http.get(`${quotationSuppliersEndpoint}/${sid}/products`, {
          params: { page: 1, limit: PAGE_LIMIT },
        });
        return response.data;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    }))
  );

  // Rebuild only when a fetch actually lands (dataUpdatedAt changes) — the
  // `results` array identity changes every render, so it can't be the dep.
  const updatedKey = results.map((r) => r.dataUpdatedAt).join(',');

  return useMemo(() => {
    const index = new Map<string, QuotationSupplierProduct>();
    results.forEach((r) => {
      (r.data as Paginated<QuotationSupplierProduct> | undefined)?.data?.forEach((p) =>
        index.set(quotableProductKey(p.supplierId, p.masterProductId ?? p.id), p)
      );
    });
    return index;
  }, [updatedKey]);
}
